import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  FormControlLabel, 
  Switch, 
  Typography,
  InputAdornment,
  IconButton,
  Stack
} from '@mui/material';
import { ColorLens as ColorIcon } from '@mui/icons-material';
import { HomeExpenseGroupDto } from '../../types/homeExpenses';
import CategorySelector from '../../components/CategorySelector';

interface HomeExpenseGroupFormProps {
  initial?: HomeExpenseGroupDto;
  onSubmit: (data: {
    name: string;
    description?: string;
    isIncomeGroup: boolean;
    color?: string;
    categoryId?: string;
    isShoppingDefault: boolean;
  }) => void;
  onCancel?: () => void;
  loading?: boolean;
}

// Common colors for expense groups
const PRESET_COLORS = [
  '#f44336', // Red - for expenses
  '#e91e63', // Pink
  '#9c27b0', // Purple
  '#673ab7', // Deep Purple
  '#3f51b5', // Indigo
  '#2196f3', // Blue
  '#03a9f4', // Light Blue
  '#00bcd4', // Cyan
  '#009688', // Teal
  '#4caf50', // Green - for income
  '#8bc34a', // Light Green
  '#cddc39', // Lime
  '#ffeb3b', // Yellow
  '#ffc107', // Amber
  '#ff9800', // Orange
  '#ff5722', // Deep Orange
  '#795548', // Brown
  '#607d8b', // Blue Grey
];

const HomeExpenseGroupForm: React.FC<HomeExpenseGroupFormProps> = ({ 
  initial, 
  onSubmit, 
  onCancel, 
  loading = false 
}) => {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [isIncomeGroup, setIsIncomeGroup] = useState(initial?.isIncomeGroup || false);
  const [color, setColor] = useState(initial?.color || '');
  const [categoryId, setCategoryId] = useState(initial?.category?.publicId || '');
  const [isShoppingDefault, setIsShoppingDefault] = useState(initial?.isShoppingDefault || false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    const submitData = { 
      name: name.trim(), 
      description: description.trim() || undefined, 
      isIncomeGroup,
      color: color || undefined,
      categoryId: categoryId || undefined,
      isShoppingDefault: isShoppingDefault && !isIncomeGroup
    };
    
    console.log('[DEBUG HomeExpenseGroupForm] Submitting data:', submitData);
    console.log('[DEBUG HomeExpenseGroupForm] CategoryId value:', categoryId);
    console.log('[DEBUG HomeExpenseGroupForm] Initial category:', initial?.category);
    
    onSubmit(submitData);
  };

  const handleColorSelect = (selectedColor: string) => {
    setColor(selectedColor);
    setShowColorPicker(false);
  };

  // Auto-suggest color based on income/expense type
  const getSuggestedColor = () => {
    if (!color) {
      return isIncomeGroup ? '#4caf50' : '#f44336'; // Green for income, Red for expense
    }
    return color;
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
      <Stack spacing={3}>
        <TextField
          label="Nome do grupo"
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          required
          disabled={loading}
          placeholder="ex.: Supermercado, Serviços, Salário"
        />
        
        <TextField
          label="Descrição"
          value={description}
          onChange={e => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
          disabled={loading}
          placeholder="Descrição opcional deste grupo"
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={isIncomeGroup}
              onChange={e => setIsIncomeGroup(e.target.checked)}
              disabled={loading}
            />
          }
          label={
            <Box>
              <Typography variant="body1">
                {isIncomeGroup ? 'Grupo de receitas' : 'Grupo de despesas'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isIncomeGroup ? 'Este grupo representa fontes de receita' : 'Este grupo representa categorias de despesa'}
              </Typography>
            </Box>
          }
        />
        
        {!isIncomeGroup && (
          <FormControlLabel
            control={
              <Switch
                checked={isShoppingDefault}
                onChange={e => setIsShoppingDefault(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <Box>
                <Typography variant="body1">Grupo das compras</Typography>
                <Typography variant="caption" color="text.secondary">
                  Pré-selecionado ao registar a despesa de uma ida às compras. Só um grupo pode estar marcado; marcar este desmarca o anterior.
                </Typography>
              </Box>
            }
          />
        )}

        <CategorySelector
          value={categoryId}
          onChange={(value) => setCategoryId(value || '')}
          disabled={loading}
          label="Categoria"
        />
        
        <Box>
          <TextField
            label="Cor"
            value={color}
            onChange={e => setColor(e.target.value)}
            fullWidth
            disabled={loading}
            placeholder={getSuggestedColor()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box 
                    sx={{ 
                      width: 20, 
                      height: 20, 
                      borderRadius: 1, 
                      backgroundColor: color || getSuggestedColor(),
                      border: '1px solid #ccc'
                    }} 
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton 
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    disabled={loading}
                    size="small"
                  >
                    <ColorIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          
          {showColorPicker && (
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              mt: 1, 
              p: 2, 
              border: '1px solid #ccc', 
              borderRadius: 1 
            }}>
              {PRESET_COLORS.map((presetColor) => (
                <Box
                  key={presetColor}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    backgroundColor: presetColor,
                    cursor: 'pointer',
                    border: color === presetColor ? '3px solid #000' : '1px solid #ccc',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}
                  onClick={() => handleColorSelect(presetColor)}
                />
              ))}
            </Box>
          )}
        </Box>
      </Stack>
      
      <Box mt={3} display="flex" gap={2} justifyContent="flex-end">
        {onCancel && (
          <Button 
            variant="outlined" 
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
        )}
        <Button 
          type="submit" 
          variant="contained" 
          color="primary"
          disabled={loading || !name.trim()}
        >
          {loading ? 'A guardar...' : (initial ? 'Atualizar grupo' : 'Criar grupo')}
        </Button>
      </Box>
    </Box>
  );
};

export default HomeExpenseGroupForm;
